/*
 * The admin menu (AdminLayout sidebar, desktop and mobile drawer) and who may
 * open what. Each item names its own section, so adding a page is one line
 * here — there are no index lists to keep in sync. adminNav.test.ts fails when
 * an /admin route has no entry; a route that is deliberately unlisted sets
 * `meta.navHidden`.
 *
 * Roles: ADMIN sees everything. KYC_ADMIN (compliance reviewer) sees only the
 * KYC queue and the read-only user view it links to — the backend enforces
 * the same split (KycReviewerGuard vs AdminGuard).
 */
export type StaffRole = 'ADMIN' | 'KYC_ADMIN';

export const ADMIN_NAV_SECTIONS = ['COMMERCE', 'MANAGEMENT', 'CONTENT', 'SYSTEM'] as const;
export type AdminNavSection = (typeof ADMIN_NAV_SECTIONS)[number];

export interface AdminNavItem {
  to: string;
  label: string;
  /** Text glyph, the admin's icon style. */
  icon: string;
  section: AdminNavSection;
  /** Who sees it; default ADMIN only. */
  roles?: StaffRole[];
}

export const ADMIN_NAV: AdminNavItem[] = [
  { to: '/admin', label: 'Dashboard', icon: '◈', section: 'COMMERCE' },
  { to: '/admin/orders', label: 'Orders', icon: '▤', section: 'COMMERCE' },
  { to: '/admin/listings', label: 'Listings', icon: '▦', section: 'COMMERCE' },
  { to: '/admin/payments', label: 'Payments', icon: '◇', section: 'COMMERCE' },
  { to: '/admin/payment-methods', label: 'Payment methods', icon: '▣', section: 'COMMERCE' },
  { to: '/admin/users', label: 'Users', icon: '◉', section: 'MANAGEMENT' },
  { to: '/admin/kyc', label: 'KYC', icon: '◎', section: 'MANAGEMENT', roles: ['ADMIN', 'KYC_ADMIN'] },
  { to: '/admin/blacklist', label: 'Blacklist', icon: '⊘', section: 'MANAGEMENT' },
  { to: '/admin/cms', label: 'CMS', icon: '▧', section: 'CONTENT' },
  { to: '/admin/meta', label: 'Meta / SEO', icon: '◬', section: 'CONTENT' },
  { to: '/admin/settings', label: 'Settings', icon: '⚙', section: 'SYSTEM' },
  { to: '/admin/ops', label: 'Ops', icon: '⏻', section: 'SYSTEM' },
  { to: '/admin/api-keys', label: 'API Keys', icon: '⚿', section: 'SYSTEM' },
  { to: '/admin/audit-log', label: 'Audit Log', icon: '▥', section: 'SYSTEM' },
];

function visibleTo(item: AdminNavItem, role: StaffRole) {
  return (item.roles ?? ['ADMIN']).includes(role);
}

/** Sidebar groups for a role; empty sections are dropped. */
export function adminNavGroups(role: StaffRole) {
  return ADMIN_NAV_SECTIONS.map((label) => ({
    label,
    items: ADMIN_NAV.filter((item) => item.section === label && visibleTo(item, role)),
  })).filter((g) => g.items.length > 0);
}

/** Admin route names a KYC_ADMIN may open (the queue and its read-only user view). */
export const KYC_ADMIN_ROUTES = ['AdminKyc', 'AdminUserDetail'];

/** Where a staff member may go inside /admin; null = allowed, else the redirect target. */
export function adminRedirect(role: string | undefined, routeName: string | undefined): string | null {
  if (role === 'ADMIN') return null;
  if (role === 'KYC_ADMIN') return routeName && KYC_ADMIN_ROUTES.includes(routeName) ? null : '/admin/kyc';
  return '/403';
}
